import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedHeading from "../../animations/AnimatedHeading";

const faqs = [
  {
    question: "What services does your company offer?",
    answer:
      "Our solutions are tailored to meet your unique business needs, improving efficiency, reducing costs, and enabling seamless digital transformation to boost growth and productivity and stay up-to-date with the latest technologies. The consultation phase and keep you informed.",
  },
  {
    question: "How can your IT solutions help my business?",
    answer:
      "We provide end-to-end IT solutions that streamline your operations, reduce overhead, and help you scale efficiently. From cloud infrastructure to custom software, we've got you covered.",
  },
  {
    question: "Do you provide support after project completion?",
    answer:
      "Yes! We offer comprehensive post-launch support packages including 24/7 monitoring, bug fixes, performance optimization, and feature updates to ensure your solution keeps running smoothly.",
  },
  {
    question: "How long does it take to implement an IT solution?",
    answer:
      "Timelines vary based on project scope and complexity. A typical project ranges from 4 to 16 weeks. We provide a detailed project plan with milestones during our discovery phase.",
  },
  {
    question: "Is my data secure with your solutions?",
    answer:
      "Absolutely. We follow industry best practices and compliance standards including GDPR, ISO 27001, and SOC 2. All data is encrypted at rest and in transit with regular security audits.",
  },
];

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section
      className="
        w-full
        overflow-hidden
        theme-bg-primary
        py-12
        px-4
        font-saira
        min-[375px]:px-5
        min-[375px]:py-14
        sm:px-6
        sm:py-16
        md:px-8
        md:py-20
        lg:px-6
        lg:py-20
        xl:px-8
      "
    >
      <div
        className="
          mx-auto
          grid
          w-full
          max-w-7xl
          grid-cols-1
          items-start
          gap-10
          sm:gap-12
          md:gap-14
          lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]
          lg:gap-12
          xl:gap-14
        "
      >
        {/* =========================================================
            LEFT COLUMN
        ========================================================== */}

        <div
          className="
            flex
            w-full
            min-w-0
            flex-col
            justify-between
            gap-8
            text-center
            min-[375px]:gap-9
            sm:gap-10
            lg:text-left
          "
        >
          {/* =======================================================
              INTRO CONTENT
          ======================================================= */}

          <div className="w-full min-w-0">
            {/* Label */}

            <motion.p
              initial={{
                opacity: 0,
                y: 14,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: 0.45,
              }}
              className="
                mb-5
                brand-color
                text-xs
                font-bold
                uppercase
                tracking-[0.22em]
                min-[375px]:tracking-[0.25em]
                sm:mb-6
                sm:text-sm
                sm:tracking-[0.3em]
              "
            >
              [ READ FAQS ]
            </motion.p>

            {/* Heading */}

            <div className="w-full min-w-0">
              <AnimatedHeading
                as="h2"
                parts={[
                  "Quick Answers For",
                  "Your Important",
                  "Questions",
                ]}
                className="
                  mx-auto
                  w-full
                  max-w-3xl
                  font-saira
                  text-[30px]
                  font-semibold
                  leading-[1.15]
                  theme-text-primary
                  min-[375px]:text-[34px]
                  sm:text-4xl
                  sm:leading-tight
                  md:text-5xl
                  lg:mx-0
                  lg:max-w-none
                  lg:text-6xl
                "
              />
            </div>
          </div>

          {/* =======================================================
              CALL CARD
          ======================================================= */}

          <motion.div
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.5,
              delay: 0.2,
            }}
            className="
              theme-card
              theme-border
              w-full
              min-w-0
              rounded-2xl
              border
              p-5
              shadow-lg
              min-[375px]:p-6
              sm:p-7
              lg:p-7
            "
          >
            <p
              className="
                theme-text-muted
                mb-5
                text-sm
                leading-7
                sm:mb-6
                sm:leading-relaxed
                lg:text-left
              "
            >
              Getting started is easy! Simply reach out us through our contact
              form or give us a call &amp; we'll schedule.
            </p>

            {/* =====================================================
                PHONE INFORMATION
            ===================================================== */}

            <div
              className="
                theme-bg-secondary
                theme-border
                flex
                w-full
                min-w-0
                flex-col
                items-center
                gap-4
                rounded-xl
                border
                px-4
                py-4
                min-[375px]:px-5
                sm:flex-row
                sm:justify-between
                sm:gap-3
                lg:items-center
              "
            >
              {/* Phone */}

              <div
                className="
                  min-w-0
                  max-w-full
                  text-center
                  sm:text-left
                "
              >
                <p
                  className="
                    theme-text-muted
                    mb-1
                    text-xs
                  "
                >
                  Call us now
                </p>

                <a
                  href="tel:+918328892097"
                  className="
                    theme-text-primary
                    block
                    max-w-full
                    truncate
                    text-sm
                    font-bold
                    underline
                    underline-offset-2
                    transition-colors
                    hover:brand-color
                    min-[375px]:text-base
                  "
                >
                  +91 83288 92097
                </a>
              </div>

              {/* ===================================================
                  AVATAR + PHONE ICON
              =================================================== */}

              <div
                className="
                  flex
                  shrink-0
                  items-center
                  justify-center
                "
              >
                <img
                  src="https://randomuser.me/api/portraits/men/32.jpg"
                  alt="Support"
                  loading="lazy"
                  decoding="async"
                  className="
                    theme-border
                    h-10
                    w-10
                    rounded-full
                    border-2
                    object-cover
                    min-[375px]:h-11
                    min-[375px]:w-11
                  "
                />

                <div
                  className="
                    brand-bg
                    -ml-2
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    min-[375px]:h-10
                    min-[375px]:w-10
                  "
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="none"
                    stroke="var(--bg-secondary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.67A2 2 0 012 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* =========================================================
            RIGHT COLUMN — FAQ ACCORDION
        ========================================================== */}

        <div
          className="
            flex
            w-full
            min-w-0
            flex-col
            gap-3
          "
        >
          {faqs.map((faq, i) => {
            const isOpen = openIndex === i;

            return (
              <motion.div
                key={i}
                initial={{
                  opacity: 0,
                  y: 18,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.45,
                  delay: i * 0.07,
                }}
                className={`
                  faq-card
                  w-full
                  min-w-0
                  overflow-hidden
                  rounded-xl
                  border
                  transition-all
                  duration-300
                  sm:rounded-2xl
                  ${
                    isOpen
                      ? "theme-card-secondary border-[var(--brand-primary)]/20"
                      : "theme-card border-[var(--border-primary)] hover:border-[var(--brand-primary)]/20"
                  }
                `}
              >
                {/* =================================================
                    QUESTION ROW
                ================================================== */}

                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="
                    flex
                    w-full
                    min-w-0
                    cursor-pointer
                    items-center
                    justify-between
                    gap-3
                    border-0
                    bg-transparent
                    px-4
                    py-4
                    text-left
                    min-[375px]:px-5
                    min-[375px]:py-5
                    sm:px-6
                    sm:py-5
                    md:gap-4
                  "
                >
                  {/* Question */}

                  <span
                    className={`
                      syne
                      min-w-0
                      flex-1
                      pr-1
                      text-sm
                      font-bold
                      leading-6
                      break-words
                      transition-colors
                      duration-300
                      min-[375px]:text-[15px]
                      sm:pr-4
                      sm:text-base
                      sm:leading-7
                      md:text-lg
                      ${
                        isOpen
                          ? "brand-color"
                          : "theme-text-primary"
                      }
                    `}
                  >
                    {faq.question}
                  </span>

                  {/* =================================================
                      PLUS / MINUS BUTTON
                  ================================================== */}

                  <span
                    className={`
                      flex
                      h-7
                      w-7
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      transition-all
                      duration-300
                      sm:h-8
                      sm:w-8
                      ${
                        isOpen
                          ? "brand-bg"
                          : "border-[1.5px] border-[var(--brand-primary)] bg-transparent"
                      }
                    `}
                  >
                    {isOpen ? (
                      <svg
                        viewBox="0 0 24 24"
                        width="13"
                        height="13"
                        fill="none"
                        stroke="var(--bg-secondary)"
                        strokeWidth="3"
                        strokeLinecap="round"
                      >
                        <path d="M5 12h14" />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        width="13"
                        height="13"
                        fill="none"
                        stroke="var(--brand-primary)"
                        strokeWidth="3"
                        strokeLinecap="round"
                      >
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    )}
                  </span>
                </button>

                {/* =================================================
                    DIVIDER
                ================================================== */}

                {isOpen && (
                  <div
                    className="
                      theme-border
                      mx-4
                      h-px
                      border-t
                      min-[375px]:mx-5
                      sm:mx-6
                    "
                  />
                )}

                {/* =================================================
                    ANSWER
                ================================================== */}

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.3,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      style={{
                        overflow: "hidden",
                      }}
                    >
                      <p
                        className="
                          theme-text-muted
                          px-4
                          py-4
                          text-sm
                          leading-7
                          break-words
                          min-[375px]:px-5
                          min-[375px]:py-5
                          sm:px-6
                          sm:py-5
                          sm:leading-7
                        "
                      >
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}